import { AUTH_API_HOST } from "@/api/apis";
import React, { useState } from "react";
import UserIcon from "@/components/compatibility/experiences/UserIcon";
import { useTrans } from "@/utils/useTrans";
import { AuthUtils } from "@/utils/AuthUtils";
import { markCartTokenForLogout } from "@/api/CartApi";
import { useRouter } from "next/router";
import Link from "next/link";
import { Drawer } from "@/v2-components/ui/drawer";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { cn } from "@/libs/utils";
import { User } from "@/models/User";
import { Slot } from "@radix-ui/react-slot";
import TruckIcon from "@/v2-components/core/icon/truck-icon";
import TagIcon from "@/v2-components/core/icon/tag-icon";
import {
  ProfileCouponDot,
  useUsableCoupons,
} from "@/v2-components/global-navigation-bar/profile-coupon-alert";

// user login components
const UserLogin = ({ user }: { user: User }) => {
  const router = useRouter();
  const [show, setShow] = useState(false); // display state of the popover
  const mobile = useIsMobileView();

  const handleToggle = () => setShow((prev) => !prev);
  const handleClose = () => setShow(false);

  const logout = () => {
    markCartTokenForLogout(); // 로그아웃 뒤 이 브라우저의 cart token 을 바꾸게 한다(#6185)
    const logoutUrl = AuthUtils.getLogoutUrl();
    router.push(logoutUrl);
  };

  return (
    <>
      <Drawer
        className="flex h-auto max-h-[85vh] min-h-[80vh] flex-col overflow-hidden !p-0 laptop:min-h-[100vh]"
        onOpenChange={handleClose}
        open={show}
        side={mobile ? "bottom" : "right"}
      >
        <UserPopover onClose={handleClose} onLogout={logout} user={user} />
      </Drawer>
      <nav aria-label="User Menu">
        {/* 쿠폰 dot 의 기준 박스. absolute 라 아바타 레이아웃에는 영향이 없다.
            (카운트다운 툴팁은 가방 아이콘 기준으로 옮겼다 — GNB 의 가방 래퍼가 그 기준이다.) */}
        <div className="relative">
          {/* user icon click toggle*/}
          <div
            onClick={handleToggle}
            aria-expanded={show} // popup state submit
            aria-label="Open User Menu"
            className="rounded-full p-[3px] hover:bg-coolgray-60 hover:bg-opacity-20"
          >
            <UserIcon
              className={
                "min-h-6 min-w-6 laptop:min-h-6 laptop:min-w-6 laptop:text-base"
              }
              id={user.userId}
            />
          </div>
          <ProfileCouponDot />
        </div>
      </nav>
    </>
  );
};

interface UserPopoverProps {
  onClose: () => void;
  user: { userId: string; email: string };
  onLogout: () => void;
}

const Capsule = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <Slot
    className={cn(
      [
        "flex items-center justify-center",
        "rounded-full border border-coolgray-20 px-12 py-2 hover:border-coolgray-hover-20",
        "text-center text-sm font-600",
      ],
      className,
    )}
    {...props}
  />
);

const UserPopover = ({ onClose, user, onLogout }: UserPopoverProps) => {
  const { t } = useTrans(["gnb"]);
  const { userId, email } = user;
  // 아바타 dot·말풍선과 같은 기준(지금 쓸 수 있는 쿠폰)이다 — 같은 훅을 써서 두 표시가 갈리지 않게 한다.
  const { usable } = useUsableCoupons();

  return (
    <>
      <div className="flex items-center justify-between px-4 py-7 laptop:px-8">
        <p className="text-2xl capitalize">{t("Account")}</p>
        <button onClick={onClose}>
          <CrossIcon />
        </button>
      </div>
      <div className="flex grow justify-center overflow-auto pt-5 text-base text-coolgray-90 laptop:pt-20">
        <article>
          <header className="flex flex-col items-center gap-y-2.5 px-5">
            <UserIcon className="aspect-square !min-w-[72px]" id={userId} />
            <div className="flex flex-col items-center gap-y-2">
              <h2 className="text-lg">{userId}</h2>
              <p className="text-base text-coolgray-60">{email}</p>
            </div>
          </header>

          <div className="mt-[52px] grid grid-flow-row auto-rows-fr justify-center gap-y-6">
            <Capsule>
              <Link
                onClick={onClose}
                aria-label="Manage Account"
                href={`${AUTH_API_HOST}/account`}
              >
                {t("Manage Account")}
              </Link>
            </Capsule>
            <Capsule>
              {/* 쿠폰함 정본은 계정(auth) 화면이다(#5127) — 쇼핑몰 `/coupons` 는 제거됐다.
                  슬래시를 붙이지 않는 이유는 `next.config.mjs` 의 `/coupons` 리다이렉트 주석 참조.
                  쓸 수 있는 쿠폰이 없어도 항상 노출한다 — 사용·만료 이력을 보러 오는 자리이기도 하고,
                  아바타 dot·말풍선은 보유 중일 때만 떠서 그것만으로는 상시 진입점이 없다. */}
              <Link
                aria-label="Coupons"
                href={`${AUTH_API_HOST}/coupons`}
                onClick={onClose}
              >
                <div className="flex flex-nowrap items-center gap-x-2">
                  <TagIcon />
                  <p>{t("Coupons")}</p>
                  {usable.length > 0 && (
                    <span
                      data-testid="account-coupon-badge"
                      className="font-bold rounded-full bg-bhaptics-yellow px-1.5 text-xs leading-5 text-coolgray-100"
                    >
                      {usable.length}
                    </span>
                  )}
                </div>
              </Link>
            </Capsule>
            <Capsule>
              <Link
                aria-label="Order Status"
                href={`/tracking`}
                onClick={onClose}
              >
                <div className="flex flex-nowrap items-center gap-x-2">
                  <TruckIcon />
                  <p>{t("Order Status")}</p>
                </div>
              </Link>
            </Capsule>
            <Capsule>
              <button aria-label="Sign Out" onClick={onLogout}>
                {t("Sign Out")}
              </button>
            </Capsule>
          </div>
        </article>
      </div>
    </>
  );
};

export default UserLogin;
